// @ts-ignore
import qc from 'qc'
import StageData from '@ps/store/workbench/stage/stage-data'
import Konva from 'konva'
import {
  allowBeResize,
  allowBeRotation,
  allowBeSelected,
  disableDraggable,
  initNodeConfig,
  isBanEditNode,
  isKeepRatio,
  SelectedNodeMoveToTop,
  setNodeHoverClass,
  SpecName,
  SpecNodeGhostName,
  SpecSuffixName,
} from '@ps/store/workbench/stage/config/layer-config'
import { ActionContext } from 'vuex'
import { StageState } from '@ps/types/workbench/stage.type'
import { RootState } from '@ps/types/store'
import {
  convertNode2RectSize,
  getNodeByShape,
} from '@ps/store/workbench/stage/utils/helper'
import { Event, EventBus } from '@ps/utils/event-bus'
import _ from 'lodash'
import {
  hadEvent,
  isDrawLineAreaNode,
  isDrawLineNode,
  isNodeGhost,
  isTweenPositionArea,
  NodeGhostClass,
} from '@ps/bridge/qc/base'
import { initAnchorsStyle } from '@com/konva-modules/transformer-style'
import gameIframe from '@ps/bridge/iframe/iframe'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import nodePool from '@ps/bridge/qc/node-pool'
import { deg2rad } from '@com/utils/unit-converters/angle'
import { generateUUID } from '@ps/utils/game'
import getReArrangeNodes from '@ps/store/workbench/stage/utils/rearrange-nodes'
import store from '@ps/store'
import { isIvPage } from '@ps/router/config'

export default {
  // 根据真实 node 属性，新增 konva 舞台图形
  addVirtualNode(
    { dispatch }: ActionContext<StageState, RootState>,
    node: qc.Node,
  ): void {
    if (!StageData.layer) {
      console.error('当前画布没有 layer')
      return
    }
    const KonvaLayer = StageData.layer as Konva.Layer
    const RectConfig = initNodeConfig(node)
    const Rect = new Konva.Rect(RectConfig)

    // 显示的元素才绑定事件
    dispatch('addVirtualNodeListener', Rect)
    KonvaLayer.add(Rect)
    if (RectConfig.suffixConfig && RectConfig.visible) {
      const Suffix = new Konva.Circle(RectConfig.suffixConfig)
      KonvaLayer.add(Suffix)
    }
    if (RectConfig.textConfig && RectConfig.visible) {
      const Text = new Konva.Text(RectConfig.textConfig)
      KonvaLayer.add(Text)
    }
    dispatch('resetShapeLayout', node)
    // 确保 transformer 在最高层级
    StageData.transformer?.moveToTop()
  },

  /**
   * 创建虚拟节点的 ghost ( 主要用于展示动画结束状态）
   * @param {Object} param0
   * @param x 动画相对于源节点 x 偏移量
   * @param y 动画相对于源节点 y 偏移量
   * @param width 结束状态的宽度
   * @param height 结束状态的高度
   * @param rotation 结束状态的旋转角度
   * @param scaleX 结束状态的 x 方向缩放比例
   * @param scaleY 结束状态的 y 方向缩放比例
   * @param sourceNode 源节点
   */
  addVirtualNodeGhost(
    { dispatch, rootState }: ActionContext<StageState, RootState>,
    {
      x,
      y,
      width,
      height,
      rotation,
      scaleX,
      scaleY,
      sourceNode,
    }: {
      x: number
      y: number
      width: number
      height: number
      rotation: number
      scaleX: number
      scaleY: number
      sourceNode: qc.Node
    },
  ): void {
    const parentNode = sourceNode.parent
    const node = gameIframe.qcGame.add.image(parentNode)
    try {
      const gameOb = rootState.workspace.game.data.gameOb
      const atlasKey = `snapshot_${generateUUID()}`
      sourceNode.snapshotAsAtlas(atlasKey)
      const res = gameOb.assets.find(atlasKey)
      node.texture = res
    } catch (error) {
      console.log('无法生成虚拟节点预览图')
    }
    node.name = SpecNodeGhostName
    node.x = x
    node.y = y
    node.width = width
    node.height = height
    node.rotation = deg2rad(rotation)
    node.scaleX = scaleX
    node.scaleY = scaleY
    node.alpha = 0.5
    node.pivotX = sourceNode.pivotX
    node.pivotY = sourceNode.pivotY
    const editorData = editorScript.getScript(node)
    if (editorData) {
      editorData.$data.nodeType = NodeGhostClass
      editorData.$data.isTempNode = true
      editorData.$data.dragLinkageNodeUUID = sourceNode.uuid
    }
    const sourceNodeeditorData = editorScript.getScript(sourceNode)
    if (sourceNodeeditorData) {
      sourceNodeeditorData.$data.dragLinkageNodeUUID = node.uuid
    }
    dispatch('addVirtualNode', node)
  },

  /**
   * 聚焦在当前节点，其他节点均不可操作
   * @param commit
   * @param dispatch
   * @param node
   */
  focusinVirtualNode(
    { state }: ActionContext<StageState, RootState>,
    node: qc.Node,
  ): void {
    const targetUUID = node.uuid
    if (!StageData.layer) return
    const KonvaLayer = StageData.layer as Konva.Layer
    const shapes: Konva.Rect[] = KonvaLayer.find(`.${SpecName}`)
    state.isFocusin = true
    _.forEach(shapes, (shape) => {
      if (shape.id() !== targetUUID) {
        shape.draggable(false)
      } else {
        setNodeHoverClass(shape, true)
      }
    })
    const isTransformerIncludedTarget = StageData.transformer
      ?.nodes()
      .some((shape) => {
        return shape.attrs.id === targetUUID
      })
    if (!isTransformerIncludedTarget) {
      StageData.transformer?.resizeEnabled(false)
      StageData.transformer?.rotateEnabled(false)
    }
  },

  cancelFocusin({ state }: ActionContext<StageState, RootState>): void {
    state.isFocusin = false
    EventBus.$emit(Event.REFRESH_STAGE)
  },

  destroyVirtualNodeGhost({
    dispatch,
    rootState,
  }: ActionContext<StageState, RootState>): void {
    const currentSceneNode = rootState.workspace.sceneManager.currentSceneNode
    if (!currentSceneNode) return
    const targetNode = currentSceneNode.getChild(SpecNodeGhostName)
    if (!targetNode) return
    const editorData = editorScript.getScript(targetNode)
    // 通过 dragLinkageNodeUUID 获取源节点
    const sourceNode = nodePool.find(
      editorData!.$data.dragLinkageNodeUUID as string,
    )
    const sourceNodeeditorData = editorScript.getScript(sourceNode)
    if (sourceNodeeditorData) {
      sourceNodeeditorData.$data.dragLinkageNodeUUID = false
    }
    if (targetNode) {
      dispatch('destroyShape', targetNode)
      targetNode.removeSelf()
    }
  },

  disableLinkageNode(
    { dispatch }: ActionContext<StageState, RootState>,
    node: qc.Node,
  ): void {
    const KonvaLayer = StageData.layer as Konva.Layer
    const rmLinkage = (node: qc.Node) => {
      const editorData = editorScript.getScript(node)
      if (editorData) {
        editorData.$data.dragLinkageNodeUUID = false
      }
      KonvaLayer.findOne(`#${node.uuid}`).attrs.dragLinkage = false
    }
    const editorData = editorScript.getScript(node)
    if (editorData!.$data.dragLinkageNodeUUID) {
      // 通过 dragLinkageNodeUUID 获取幽灵节点
      const ghostNode = nodePool.find(
        editorData!.$data.dragLinkageNodeUUID as string,
      )
      dispatch('focusinVirtualNode', ghostNode)
      rmLinkage(ghostNode)
    }
    rmLinkage(node)
  },

  enableLinkageNode(
    { dispatch }: ActionContext<StageState, RootState>,
    node: qc.Node,
  ): void {
    const ghostNode = node.parent.getChild(SpecNodeGhostName)
    const editorData = editorScript.getScript(node)
    const ghostEditorData = editorScript.getScript(ghostNode)
    const KonvaLayer = StageData.layer as Konva.Layer
    KonvaLayer.findOne(`#${node.uuid}`).attrs.dragLinkage = ghostNode.uuid
    KonvaLayer.findOne(`#${ghostNode.uuid}`).attrs.dragLinkage = node.uuid
    editorData!.$data.dragLinkageNodeUUID = ghostNode.uuid
    ghostEditorData!.$data.dragLinkageNodeUUID = node.uuid
    dispatch('cancelFocusin')
  },

  // 根据当前节点信息与画布信息重置节点布局位置
  resetShapeLayout(
    { state, rootState }: ActionContext<StageState, RootState>,
    node: qc.Node,
  ) {
    const activeNodesList = rootState.workspace.game.activeNodeList
    // 如果当前节点处于用户操作下，不自动更新 shapeLayout 避免抖动
    // 不能随便删除这个逻辑，因为节点在操作过程中，会有一些吸附和对齐的操作，这些操作也会触发节点位置变动，如果自动更新的话，会出现抖动情况，其实按照现在的数据流来说，用户主动操作的时候，数据流已经按照 shape 的位置信息去更新节点，不需要再从节点反向影响 shape
    if (
      state.isTransform &&
      activeNodesList.some((item: qc.Node) => item.uuid === node.uuid)
    ) {
      return
    }
    const { uuid } = node
    const sceneScale: number = store.state.workspace.game.device.scale
    const { x, y } = state.sceneOffset
    const KonvaLayer = StageData.layer as Konva.Layer
    const shape = KonvaLayer.findOne(`#${uuid}`)
    if (shape) {
      const RectSize = convertNode2RectSize(
        node,
        {
          x: x,
          y: y,
        },
        sceneScale,
      )
      const RectSizeKeyMap = {
        x: 'attrs.x',
        y: 'attrs.y',
        'offset.x': 'attrs.offsetX',
        'offset.y': 'attrs.offsetY',
        width: 'attrs.width',
        height: 'attrs.height',
        scaleX: 'attrs.scaleX',
        scaleY: 'attrs.scaleY',
        rotation: 'attrs.rotation',
      }
      let transformChanged = false
      // 只有节点显示位置有变动才做刷新操作
      _.forEach(RectSizeKeyMap, (shapeKey: string, key: string) => {
        if (
          !transformChanged &&
          _.get(RectSize, key) !== _.get(shape, shapeKey)
        ) {
          transformChanged = true
        }
      })
      if (transformChanged) {
        const RectConfig = initNodeConfig(node)
        shape.setAttrs({
          ...RectConfig,
          ...RectSize,
          skewX: 0,
          skewY: 0,
        })
      }
      if (shape.attrs.textConfig) {
        const text = KonvaLayer.findOne(`#${shape.attrs.textConfig.id}`)
        if (text) {
          text.setAttrs({
            x: shape.x() - (shape.width() * shape.scaleX()) / 2,
            y: shape.y() - (shape.height() * shape.scaleY()) / 2,
          })
        }
      }
      if (shape.attrs.suffixConfig) {
        const suffix = KonvaLayer.findOne(`#${shape.attrs.suffixConfig.id}`)
        if (suffix) {
          suffix.setAttrs({
            x: shape.x() - (shape.width() * shape.scaleX()) / 2,
            y: shape.y() - (shape.height() * shape.scaleY()) / 2,
          })
        }
      }
    }
  },

  resetAllShapeLayout(
    { rootState, dispatch }: ActionContext<StageState, RootState>,
    node?: qc.Node,
  ): void {
    const targetNode = node || rootState.workspace.sceneManager.currentSceneNode
    const KonvaLayer = StageData.layer as Konva.Layer
    if (targetNode) {
      // 每次刷新的时候需要渲染所有节点，隐藏节点在 resetShapeLayout 的时候会隐藏，所以不需要在 getReArrangeNodes 时做处理
      const currentNodeList = getReArrangeNodes(targetNode, true)
      currentNodeList.forEach((node) => {
        if (allowBeSelected(node)) {
          // 如果不含有这个节点，需要新增
          if (!KonvaLayer.findOne(`#${node.uuid}`)) {
            dispatch('addVirtualNode', node)
          } else {
            dispatch('resetShapeLayout', node)
          }
        }
      })
    }
  },

  // 点击 konva.rect 更新 activenode 逻辑
  updateSelectionByShape(
    { dispatch, rootState }: ActionContext<StageState, RootState>,
    { shape, multiple }: { shape: Konva.Rect; multiple: false },
  ): void {
    if (!shape || !shape.attrs.id) {
      console.error('当前选择节点非自定义节点')
      return
    }
    const node = getNodeByShape(shape)
    if (isBanEditNode(node) || isNodeGhost(node)) {
      console.warn('锁定节点/幽灵节点无法选中')
      return
    }
    // 多选起止区域必须是之前的activeNodeList里的节点都是起止区域才能多选
    const activeNodesList = rootState.workspace.game.activeNodeList
    if (
      activeNodesList.some((node: qc.Node) => isDrawLineNode(node)) &&
      isDrawLineAreaNode(node)
    ) {
      multiple = false
    }
    if (
      activeNodesList.some((node: qc.Node) => isDrawLineAreaNode(node)) &&
      !isDrawLineAreaNode(node)
    ) {
      multiple = false
    }
    // 互动视频项目不支持多选元素
    if (isIvPage()) {
      multiple = false
    }
    dispatch(
      'workspace/game/updateActiveNode',
      { node, multiple: multiple },
      { root: true },
    )
    // 滚动左侧图层到最高级
    EventBus.$emit(Event.LAYER_SCROLL_TO_NODE)
  },

  // 框选更新 activenode 逻辑
  updateSelectionByShapeList(
    { dispatch, rootState }: ActionContext<StageState, RootState>,
    { shapes, metaPressed }: { shapes: Konva.Rect[]; metaPressed: boolean },
  ): void {
    // 如果是 shift 反选，不会清空
    if (!metaPressed && _.isEmpty(shapes)) {
      dispatch(
        'workspace/game/clearActiveNode',
        { record: true },
        { root: true },
      )
      return
    }
    const nodes: qc.Node[] = []
    const activeNodesList = rootState.workspace.game.activeNodeList
    if (metaPressed) {
      activeNodesList.forEach((node: qc.Node) => {
        if (!shapes.some((shape) => shape.attrs.id === node.uuid)) {
          nodes.push(node)
        }
      })
    } else {
      shapes.forEach((shape) => {
        const node = getNodeByShape(shape)
        if (isBanEditNode(node) || isNodeGhost(node)) {
          console.warn('锁定节点/幽灵节点无法选中', node)
        } else {
          nodes.push(node)
        }
      })
    }
    dispatch('workspace/game/updateActiveNodeList', { nodes }, { root: true })
    // 滚动左侧图层到最高级
    EventBus.$emit(Event.LAYER_SCROLL_TO_NODE)
  },

  // 通过更新 node 来更新 画布选中 逻辑
  updateSelectionByNodeList(
    { dispatch }: ActionContext<StageState, RootState>,
    nodes: qc.Node[],
  ): void {
    const KonvaTransformer = StageData.transformer as Konva.Transformer
    let lockStatus = false
    let noDraggable = false
    const lighted = !!nodes.find((node) => hadEvent(node))
    const transNodes: Konva.Rect[] = []
    const KonvaLayer = StageData.layer as Konva.Layer
    if (nodes && nodes.length) {
      // 如果当前集合中没有这个虚拟节点，则新增
      nodes.forEach((node) => {
        if (allowBeSelected(node)) {
          if (!KonvaLayer.findOne(`#${node.uuid}`)) {
            console.warn('当前选择节点不在画布中', node)
            dispatch('addVirtualNode', node)
          }
          if (disableDraggable(node)) {
            noDraggable = true
          }
          // 如果包含锁定元素，则不允许拖拽与缩放
          if (isBanEditNode(node) && !isTweenPositionArea(node)) {
            lockStatus = true
          } else {
            transNodes.push(KonvaLayer.findOne(`#${node.uuid}`))
            setNodeHoverClass(KonvaLayer.findOne(`#${node.uuid}`), false)
          }
        }
      })
      KonvaTransformer.nodes(transNodes)
      if (lockStatus || noDraggable) {
        KonvaTransformer.resizeEnabled(false)
        KonvaTransformer.rotateEnabled(false)
      } else {
        KonvaTransformer.resizeEnabled(allowBeResize(nodes))
        KonvaTransformer.rotateEnabled(allowBeRotation(nodes))
        if (isKeepRatio(nodes)) {
          KonvaTransformer.keepRatio(true)
        } else {
          KonvaTransformer.keepRatio(false)
        }
      }
    } else {
      KonvaTransformer.nodes(transNodes)
    }
    dispatch('moveSelectedShapeToTop', { shapes: transNodes })
    initAnchorsStyle(KonvaTransformer, { lighting: lighted })
  },

  toggleShapeVisible(
    { dispatch, rootState }: ActionContext<StageState, RootState>,
    { node }: { node: qc.Node },
  ): void {
    const activeNodesList = rootState.workspace.game.activeNodeList
    const KonvaLayer = StageData.layer as Konva.Layer
    const shapes = KonvaLayer.find(`.${SpecName}`)
    shapes.forEach((shape) => {
      if (node.uuid === shape.id()) {
        const visibleState = !isBanEditNode(node)
        shape.visible(visibleState)
        shape.draggable(visibleState)
        setTimeout(() => {
          // 隐藏之后需要根据最新选择状态
          dispatch('updateSelectionByNodeList', activeNodesList)
        })
      }
    })
  },

  toggleShapeListVisible(
    { dispatch }: ActionContext<StageState, RootState>,
    { nodes }: { nodes: qc.Node[] },
  ): void {
    if (_.isEmpty(nodes)) {
      console.error('传参没有内容')
      return
    }
    nodes.forEach((node) => {
      dispatch('toggleShapeVisible', {
        node: node,
      })
    })
  },

  destroyShape(
    state: ActionContext<StageState, RootState>,
    node: qc.Node,
  ): void {
    const KonvaTransformer = StageData.transformer as Konva.Transformer
    const nodes = KonvaTransformer.nodes().slice()
    const KonvaLayer = StageData.layer as Konva.Layer
    const shapes = KonvaLayer.find(`.${SpecName}`)
    shapes.forEach((shape) => {
      if (node.uuid === shape.id()) {
        nodes.splice(nodes.indexOf(shape), 1)
        KonvaTransformer.nodes(nodes)
        shape.destroy()
      }
    })
  },

  destroyAllShape({ commit }: ActionContext<StageState, RootState>): void {
    commit('clearSelection')
    const KonvaLayer = StageData.layer as Konva.Layer
    KonvaLayer.find(`.${SpecName}`).forEach((shape) => {
      shape.destroy()
    })
    KonvaLayer.find(`.${SpecSuffixName}`).forEach((shape) => {
      shape.destroy()
    })
  },

  moveSelectedShapeToTop(
    state: ActionContext<StageState, RootState>,
    { shapes }: { shapes: Konva.Rect[] },
  ): void {
    if (!SelectedNodeMoveToTop) return
    // 如果选中的节点对象存在内容，先回滚节点的层级
    if (!_.isEmpty(StageData.selectedShape)) {
      _.forEach(StageData.selectedShape, (shape: Konva.Rect, zIndex) => {
        shape.setZIndex(zIndex)
      })
    }
    StageData.selectedShape = {}
    const highZIndex = StageData.transformer?.getZIndex()
    if (shapes.length) {
      shapes.forEach((shape) => {
        StageData.selectedShape[shape.getZIndex()] = shape
        shape.setZIndex(highZIndex)
      })
    }
  },
}
